import { FC, useCallback, useRef, useState } from "react";
import styles from "./CatalogMap.module.scss";
import { Button, CircleButton, ToggleButton } from "@/lib/ui/components/Buttons";
import { modal } from "@/lib/ui/components/Modal";
import { getWordEnding } from "@/lib/utils/getWordEnding";
import { useTranslation } from "next-i18next";
import {
  phuketDistrictOptions,
  phuketBeachesOptions,
  samuiBeachesOptions,
  samuiDistrictOptions,
} from "../CatalogFilter/constants";
import { CatalogCheckboxBlock } from "../CatalogFilter/CatalogCheckboxBlock";
import { PhuketMapWithBeaches } from "@/lib/ui/components/PhuketMapWithBeaches";
import cl from "classnames";
import { useWindowSize } from "@/lib/hooks/useWindowSize";
import { TabPanel, Tabs } from "react-tabs";
import { RenderTabList } from "@/lib/ui/components/Gallery/GalleryModal";
import { P2 } from "@/lib/ui/components/Typography";
import { CatalogFilterModal } from "../CatalogFilterModal";
import { useCatalogFilter } from "@/lib/hooks/useCatalogFilter";
import { OptionsBlock } from "../CatalogFilterModal/OptionsBlock";
import { useAppSelector } from "@/lib/store/app";
import { useTranslateAndDetectDisabled } from "@/lib/hooks/useFilteredProperties";
import { SamuiMapWithBeaches } from "@/lib/ui/components/SamuiMapWithBeaches";
import { ArrowIcon } from "@/lib/ui/icons";

const highlightCheckbox = (id: string, method: "set" | "remove", isSamui?: boolean) => {
  const mapDistrict = document.querySelector("path#" + id) as HTMLElement;
  const additionalDistricts = [];
  if (id === "BANG_TAO") {
    additionalDistricts.push(document.querySelector("path#LAGOON") as HTMLElement);
    additionalDistricts.push(document.querySelector("path#LAYAN") as HTMLElement);
  } else if (id === "RAWAI") {
    additionalDistricts.push(document.querySelector("path#NAI_HARN") as HTMLElement);
  } else if (id === "KARON") {
    additionalDistricts.push(document.querySelector("path#KATA") as HTMLElement);
    additionalDistricts.push(document.querySelector("path#KATA_NOI") as HTMLElement);
  }
  const districts = [mapDistrict, ...additionalDistricts];
  districts.forEach((d) => {
    if (!d) return;
    if (method === "set") {
      d.style.fill = `url("#blueGradient${isSamui ? "Samui" : ""}FilterMap")`;
    } else {
      d.style.removeProperty("fill");
    }
  });
};

export const CatalogMap: FC<{ isInAllFilters?: boolean; includeSelectAll?: boolean }> = ({
  isInAllFilters,
  includeSelectAll,
}) => {
  const quantity = useAppSelector((state) => state.catalog.filteredQuantity);
  const [isShow, setIsShow] = useState(false);
  const {
    t,
    i18n: { language },
  } = useTranslation("index");

  const { setParam, query } = useCatalogFilter();
  const defaultIndex = query.samuiDistrict && !query.phuketDistrict ? 1 : 0;

  const { width: windowWidth } = useWindowSize();
  const isTabAnimated = useRef(false);
  const [selected, setSelected] = useState(defaultIndex);

  const translateAndDetectDisabled = useTranslateAndDetectDisabled();

  const onSelect = useCallback((index: number) => {
    setSelected(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleShowBeaches = () => {
    setIsShow((s) => !s);
  };

  return (
    <Tabs
      tabIndex={selected}
      defaultIndex={defaultIndex}
      onSelect={onSelect}
      forceRenderTabPanel={false}
      className={cl(
        styles.map,
        isInAllFilters && styles["map--all"],
        includeSelectAll && styles["map--selectAll"],
      )}
    >
      {!isInAllFilters && (
        <div className={styles.map__top}>
          <CircleButton
            name="close modal"
            as={"button"}
            onClick={() => modal.open(<CatalogFilterModal />, { isFilterModal: true })}
            content={<ArrowIcon />}
            theme="mixed"
            className={styles["map__back--mobile"]}
          />
          <P2>{t("islandAndDistrict")}</P2>
        </div>
      )}
      <div
        className={cl(
          styles.map__content,
          isInAllFilters && styles["map__content--all"],
          selected === 1 && styles["map__content--samui"],
        )}
      >
        <div className={styles.map__left}>
          {RenderTabList(
            [t("locations.PHUKET"), t("locations.SAMUI")],
            isTabAnimated,
            windowWidth,
            selected,
            "dark",
          )}
          <TabPanel className={styles.tabPanel}>
            <CatalogCheckboxBlock
              className={cl(styles.map__form, isInAllFilters && styles["map__form--all"])}
              onMouseEnter={(id) => highlightCheckbox(id, "set")}
              onMouseLeave={(id) => highlightCheckbox(id, "remove")}
              items={translateAndDetectDisabled(
                phuketDistrictOptions,
                "phuketDistrict",
                true,
              )}
              isTwoColumns
              catalogQuery="phuketDistrict"
              {...((isInAllFilters || includeSelectAll) && {
                selectAllCheckbox: { label: t("catalog.wholeIsland") },
              })}
            />
          </TabPanel>
          <TabPanel className={styles.tabPanel}>
            <CatalogCheckboxBlock
              className={styles.map__form}
              items={translateAndDetectDisabled(
                samuiDistrictOptions,
                "samuiDistrict",
                true,
              )}
              onMouseEnter={(id) => highlightCheckbox(id, "set", true)}
              onMouseLeave={(id) => highlightCheckbox(id, "remove", true)}
              isTwoColumns
              catalogQuery="samuiDistrict"
              {...((isInAllFilters || includeSelectAll) && {
                selectAllCheckbox: { label: t("catalog.wholeIsland") },
              })}
            />
          </TabPanel>
          {includeSelectAll && (
            <div className={styles.map__beaches}>
              <OptionsBlock
                items={translateAndDetectDisabled(phuketBeachesOptions, "phuketBeaches")}
                catalogQuery="phuketBeaches"
                title={t("phuketBeaches")}
              />
              <OptionsBlock
                items={translateAndDetectDisabled(samuiBeachesOptions, "samuiBeaches")}
                catalogQuery="samuiBeaches"
                title={t("samuiBeaches")}
              />
            </div>
          )}
          {!isInAllFilters && !includeSelectAll && (
            <Button
              as="button"
              className={styles.map__button}
              size="lg"
              theme="dark"
              disabled={quantity === 0}
              label={`${t("show")} ${quantity} ${t(
                getWordEnding(
                  quantity,
                  ["declinable.offer", "declinable.offers", "declinable.offersMore"],
                  language as "ru" | "en",
                ),
              )}`}
              onClick={() => modal.close()}
            />
          )}
        </div>
        <div
          className={cl(
            styles.map__right,
            isInAllFilters && styles["map__right--all"],
            selected === 1 && styles["map__right--samui"],
          )}
        >
          {selected === 0 ? (
            <PhuketMapWithBeaches isShow={isShow} />
          ) : (
            <SamuiMapWithBeaches isShow={isShow} />
          )}
          <ToggleButton
            className={styles.map__show}
            as="button"
            size="md"
            theme="secondary"
            isActive={isShow ? false : true}
            label={isShow ? t("catalog.hideBeaches") : t("catalog.showBeaches")}
            onClick={() => toggleShowBeaches()}
          />
        </div>
      </div>
    </Tabs>
  );
};
